import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, PlayCircle, Book, PenTool, ClipboardList,
  Search, Video, Calendar,
  Clock, Lock, GraduationCap, Loader2, ChevronRight, X,
} from 'lucide-react';
import { fetchSubjects, fetchAllClasses } from '../services/api';
import JoinClassButton from '../components/JoinClassButton';

// ── Syllabus themes ────────────────────────────────────────────────────────────
const SYLLABUS_MAP = { local: 'Local', cambridge: 'Cambridge', edexcel: 'Edexcel' };

const THEME = {
  Local: {
    gradient: 'from-green-500/15 via-green-500/5 to-transparent',
    border:   'border-green-500/20',
    accent:   'text-green-400',
    bg:       'bg-green-500/10',
    badgeCls: 'bg-green-500/10 text-green-400 border border-green-500/20',
    tab:      'bg-green-500/10 border-green-500/25 text-green-400',
    desc:     'Sri Lankan national curriculum for O/L and A/L examinations.',
    levels:   ['O/L', 'A/L'],
  },
  Cambridge: {
    gradient: 'from-blue-500/15 via-blue-500/5 to-transparent',
    border:   'border-blue-500/20',
    accent:   'text-blue-400',
    bg:       'bg-blue-500/10',
    badgeCls: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
    tab:      'bg-blue-500/10 border-blue-500/25 text-blue-400',
    desc:     'International curriculum for Cambridge IGCSE and AS/A-Level examinations.',
    levels:   ['IGCSE', 'AS/A-Level'],
  },
  Edexcel: {
    gradient: 'from-orange-500/15 via-orange-500/5 to-transparent',
    border:   'border-orange-500/20',
    accent:   'text-orange-400',
    bg:       'bg-orange-500/10',
    badgeCls: 'bg-orange-500/10 text-orange-400 border border-orange-500/20',
    tab:      'bg-orange-500/10 border-orange-500/25 text-orange-400',
    desc:     'Pearson Edexcel curriculum for IGCSE and International A Level examinations.',
    levels:   ['IGCSE', 'IAL'],
  },
};

const PLATFORM_STYLE = {
  'Zoom':             'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Google Meet':      'bg-green-500/10 text-green-400 border-green-500/20',
  'Google Classroom': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  'Microsoft Teams':  'bg-purple-500/10 text-purple-400 border-purple-500/20',
  'Other':            'bg-white/10 text-gray-300 border-white/20',
};

const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const fmtTime = (d) => new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

const CATEGORIES = [
  { name: 'Online Classes', icon: Video,         color: 'text-accentBlue'  },
  { name: 'Past Papers',    icon: FileText,       color: 'text-blue-400'    },
  { name: 'Revision',       icon: PenTool,        color: 'text-green-400'   },
  { name: 'Books/Notes',    icon: Book,           color: 'text-purple-400'  },
  { name: 'Videos',         icon: PlayCircle,     color: 'text-red-400'     },
  { name: 'Short Notes',    icon: ClipboardList,  color: 'text-yellow-400'  },
];

// ── Class Card ─────────────────────────────────────────────────────────────────
const ClassCard = ({ cls, userInfo, onLoginPrompt }) => {
  const isPast  = new Date(cls.scheduledAt) < new Date();
  const canJoin = userInfo?.role === 'student' || userInfo?.role === 'teacher';

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`border rounded-2xl p-5 transition-all ${isPast ? 'bg-white/3 border-white/5 opacity-60' : 'bg-white/5 border-white/10 hover:border-accentBlue/30'}`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex flex-wrap gap-2 mb-3">
            <span className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold ${PLATFORM_STYLE[cls.platform] || PLATFORM_STYLE['Other']}`}>{cls.platform}</span>
            <span className="text-[11px] px-2.5 py-1 bg-accentBlue/10 text-accentBlue rounded-full border border-accentBlue/20">{cls.subject}</span>
            <span className="text-[11px] px-2.5 py-1 bg-white/8 text-gray-300 rounded-full">{cls.level}</span>
            {isPast && <span className="text-[11px] px-2.5 py-1 bg-white/5 text-gray-500 rounded-full">Ended</span>}
          </div>
          <h4 className="font-bold text-white text-base mb-2">{cls.title}</h4>
          <div className="flex flex-wrap gap-4 text-xs text-gray-400 mb-1">
            <span className="flex items-center gap-1.5"><Calendar size={11} /> {fmtDate(cls.scheduledAt)}</span>
            <span className="flex items-center gap-1.5"><Clock size={11} /> {fmtTime(cls.scheduledAt)} · {cls.duration} min</span>
          </div>
          {cls.teacherName && <p className="text-xs text-gray-500">Teacher: {cls.teacherName}</p>}
          {cls.description && <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{cls.description}</p>}
        </div>
        <div className="shrink-0">
          {canJoin ? (
            <JoinClassButton
              classId={cls._id}
              token={userInfo.token}
              displayName={userInfo.firstName}
              disabled={isPast}
            />
          ) : (
            <button onClick={onLoginPrompt}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10 transition-colors">
              <Lock size={14} /> Login
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ── Subject Item ───────────────────────────────────────────────────────────────
const SubjectItem = ({ subject, level, category, theme, onClick }) => (
  <motion.button
    whileHover={{ x: 6 }}
    onClick={onClick}
    className="group w-full flex items-center justify-between p-4 rounded-xl bg-white/4 border border-white/8 hover:border-accentBlue/30 hover:bg-accentBlue/5 transition-all text-left"
  >
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-xl ${theme.bg} flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`}>
        <span className={`text-sm font-extrabold ${theme.accent}`}>{subject.charAt(0)}</span>
      </div>
      <div>
        <p className="font-semibold text-white text-sm group-hover:text-accentBlue transition-colors">{subject}</p>
        <p className="text-[10px] text-gray-500">{level} · {category}</p>
      </div>
    </div>
    <ChevronRight size={16} className="text-gray-600 group-hover:text-accentBlue group-hover:translate-x-1 transition-all" />
  </motion.button>
);

// ── Main Page ──────────────────────────────────────────────────────────────────
const SyllabusPage = () => {
  const { type } = useParams();
  const location  = useLocation();
  const navigate  = useNavigate();

  const currentType  = type || location.pathname.split('/').pop() || 'local';
  const syllabusName = SYLLABUS_MAP[currentType.toLowerCase()] || 'Local';
  const theme        = THEME[syllabusName] || THEME.Local;

  const [selectedCategory, setSelectedCategory] = useState('Online Classes');
  const [levelFilter, setLevelFilter]           = useState('All');
  const [search, setSearch]                     = useState('');
  const [subjectsData, setSubjectsData]         = useState({ local: [], cambridge: [], edexcel: [] });
  const [isLoading, setIsLoading]               = useState(true);
  const [onlineClasses, setOnlineClasses]       = useState([]);
  const [loadingClasses, setLoadingClasses]     = useState(false);

  const userInfo  = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const isStudent = userInfo?.role === 'student';
  const mySubjects = (userInfo?.subjects || []).map(s => s.subjectName.toLowerCase());

  // Fetch subjects
  useEffect(() => {
    fetchSubjects()
      .then(res => {
        const grouped = { local: [], cambridge: [], edexcel: [] };
        res.data.forEach(sub => {
          const key = sub.syllabus.toLowerCase();
          if (grouped[key]) grouped[key].push({ name: sub.name, level: sub.level });
        });
        setSubjectsData(grouped);
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  // Reset level filter and search when syllabus changes
  useEffect(() => {
    setLevelFilter('All');
    setSearch('');
    setSelectedCategory('Online Classes');
  }, [syllabusName]);

  // Fetch classes
  useEffect(() => {
    if (selectedCategory !== 'Online Classes') return;
    setLoadingClasses(true);
    fetchAllClasses({ syllabus: syllabusName })
      .then(res => {
        let classes = res.data || [];
        if (isStudent && mySubjects.length > 0)
          classes = classes.filter(c => mySubjects.some(s => c.subject.toLowerCase().includes(s)));
        setOnlineClasses(classes);
      })
      .catch(console.error)
      .finally(() => setLoadingClasses(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, syllabusName]);

  const allSubjects = useMemo(
    () => subjectsData[currentType.toLowerCase()] || [],
    [subjectsData, currentType]
  );

  // Level tabs for this syllabus
  const availableLevels = useMemo(() => ['All', ...new Set(allSubjects.map(s => s.level))], [allSubjects]);

  const filteredSubjects = useMemo(() => {
    return allSubjects.filter(s => {
      if (levelFilter !== 'All' && s.level !== levelFilter) return false;
      if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [allSubjects, levelFilter, search]);

  const handleSubjectClick = (subject) => {
    const catPath = selectedCategory.toLowerCase().replace(/\//g, '-').replace(/\s+/g, '-');
    navigate(`/syllabus/${currentType}/${catPath}?subject=${subject}`);
  };

  const upcomingClasses = onlineClasses.filter(c => new Date(c.scheduledAt) >= new Date());
  const pastClasses     = onlineClasses.filter(c => new Date(c.scheduledAt) <  new Date());

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 size={32} className="animate-spin text-accentBlue mb-3" />
        <p className="text-gray-500 text-sm">Loading subjects…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">

      {/* ── Hero ── */}
      <section className={`relative py-14 px-6 bg-gradient-to-br ${theme.gradient} border-b ${theme.border}`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div>
              <div className="flex flex-wrap gap-2 mb-4">
                {theme.levels.map(l => (
                  <span key={l} className={`text-xs font-bold px-3 py-1 rounded-full ${theme.badgeCls}`}>{l}</span>
                ))}
              </div>
              <h1 className={`text-4xl sm:text-5xl font-extrabold text-white tracking-tight mb-2`}>
                {syllabusName}
                <span className={`ml-3 text-2xl font-bold ${theme.accent}`}>Syllabus</span>
              </h1>
              <p className="text-gray-400 max-w-lg">{theme.desc}</p>
            </div>
            <div className="flex gap-3 shrink-0">
              <div className={`${theme.bg} border ${theme.border} rounded-2xl px-5 py-3 text-center min-w-[80px]`}>
                <p className={`text-2xl font-extrabold ${theme.accent}`}>{allSubjects.filter(s => s.level === theme.levels[0]).length}</p>
                <p className="text-[11px] text-gray-400">{theme.levels[0]}</p>
              </div>
              {theme.levels[1] && (
                <div className={`${theme.bg} border ${theme.border} rounded-2xl px-5 py-3 text-center min-w-[80px]`}>
                  <p className={`text-2xl font-extrabold ${theme.accent}`}>{allSubjects.filter(s => s.level === theme.levels[1]).length}</p>
                  <p className="text-[11px] text-gray-400">{theme.levels[1]}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* ── Category Tabs ── */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-8 scrollbar-none">
          {CATEGORIES.map(({ name, icon: Icon, color }) => (
            <button key={name} onClick={() => setSelectedCategory(name)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border whitespace-nowrap transition-all shrink-0 ${
                selectedCategory === name
                  ? 'bg-accentBlue/10 border-accentBlue/30 text-accentBlue'
                  : 'bg-white/4 border-white/10 text-gray-400 hover:border-white/20 hover:text-white'
              }`}>
              <Icon size={15} className={selectedCategory === name ? 'text-accentBlue' : color} />
              {name}
            </button>
          ))}
        </div>

        {/* ── Online Classes ── */}
        <AnimatePresence mode="wait">
          {selectedCategory === 'Online Classes' && (
            <motion.div key="classes" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Video size={18} className="text-accentBlue" /> Online Classes — {syllabusName}
                  </h2>
                  {isStudent && mySubjects.length > 0 && (
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1.5">
                      <GraduationCap size={11} /> Showing classes for your registered subjects only
                    </p>
                  )}
                  {!isStudent && (
                    <p className="text-xs text-gray-400 mt-1">Log in as a student to see personalised classes.</p>
                  )}
                </div>
              </div>

              {loadingClasses ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 size={28} className="animate-spin text-accentBlue" />
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Upcoming */}
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="w-2 h-2 rounded-full bg-green-400" />
                      <h3 className="text-sm font-bold text-gray-300 uppercase tracking-wider">
                        Upcoming ({upcomingClasses.length})
                      </h3>
                    </div>
                    {upcomingClasses.length === 0 ? (
                      <div className="py-12 text-center bg-white/3 border border-dashed border-white/10 rounded-2xl text-gray-500 text-sm">
                        {isStudent && mySubjects.length > 0 ? 'No upcoming classes for your subjects.' : 'No upcoming classes scheduled.'}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {upcomingClasses.map(cls => (
                          <ClassCard key={cls._id} cls={cls} userInfo={userInfo} onLoginPrompt={() => navigate('/login')} />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Past */}
                  {pastClasses.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <span className="w-2 h-2 rounded-full bg-gray-500" />
                        <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Past Classes ({pastClasses.length})</h3>
                      </div>
                      <div className="space-y-3 opacity-70">
                        {pastClasses.map(cls => (
                          <ClassCard key={cls._id} cls={cls} userInfo={userInfo} onLoginPrompt={() => navigate('/login')} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* ── Subject Browser ── */}
          {selectedCategory !== 'Online Classes' && (
            <motion.div key="subjects" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              {/* Header + Search */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {selectedCategory}
                    <span className="text-gray-400 font-normal text-base ml-2">· Select a subject</span>
                  </h2>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
                  <input
                    type="text"
                    placeholder="Search subjects…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 py-2.5 pl-9 pr-9 rounded-xl text-sm outline-none focus:border-accentBlue transition-all"
                    style={{ color: '#ffffff', caretColor: '#ffffff' }}
                  />
                  {search && (
                    <button onClick={() => setSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors">
                      <X size={13} />
                    </button>
                  )}
                </div>
              </div>

              {/* Level filter tabs */}
              <div className="flex gap-2 mb-6 flex-wrap">
                {availableLevels.map(l => (
                  <button key={l} onClick={() => setLevelFilter(l)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all ${
                      levelFilter === l
                        ? `${theme.tab} border`
                        : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/20 hover:text-white'
                    }`}>
                    {l}
                    <span className="ml-1.5 opacity-60">
                      ({l === 'All' ? allSubjects.length : allSubjects.filter(s => s.level === l).length})
                    </span>
                  </button>
                ))}
              </div>

              {/* Subjects list */}
              {filteredSubjects.length === 0 ? (
                <div className="py-16 text-center bg-white/3 border border-dashed border-white/10 rounded-2xl text-gray-500 text-sm">
                  No subjects found{search ? ` matching "${search}"` : ''}.
                </div>
              ) : (
                <motion.div
                  initial="hidden" animate="show"
                  variants={{ show: { transition: { staggerChildren: 0.04 } } }}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
                >
                  {filteredSubjects.map(s => (
                    <SubjectItem
                      key={`${s.name}-${s.level}`}
                      subject={s.name}
                      level={s.level}
                      category={selectedCategory}
                      theme={theme}
                      onClick={() => handleSubjectClick(s.name)}
                    />
                  ))}
                </motion.div>
              )}

              {filteredSubjects.length > 0 && (
                <p className="text-xs text-gray-600 text-center mt-6">
                  {filteredSubjects.length} subject{filteredSubjects.length !== 1 ? 's' : ''} · Click any subject to view {selectedCategory}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SyllabusPage;
